import { ArrowRight } from "lucide-react";
import { useContent } from "../content-context";
import { scrollToSection } from "../lib/scroll";
import { Eyebrow, Reveal } from "./Reveal";

export default function Accommodation() {
  const { accommodation } = useContent();
  return (
    <section id="accommodation" data-testid="accommodation-section" className="relative bg-ivory pb-24 pt-4 md:pb-32">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative aspect-[16/10] overflow-hidden group" data-testid="accommodation-main-image">
                <img
                  src={accommodation.mainImage}
                  alt={accommodation.mainAlt}
                  className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-navy/10" />
              </div>
            </Reveal>
          </div>

          <div className="flex flex-col justify-center lg:col-span-5 lg:pl-6">
            <Reveal>
              <Eyebrow>{accommodation.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-medium text-navy md:text-6xl" data-testid="accommodation-heading">
                {accommodation.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-navy/75 md:text-base" data-testid="accommodation-lead">
                {accommodation.lead}
              </p>
            </Reveal>
            <div className="mt-8">
              {accommodation.features.map((f, i) => (
                <Reveal key={f} delay={0.2 + i * 0.05}>
                  <div className="flex items-baseline gap-5 border-b border-navy/10 py-3.5" data-testid={`accommodation-feature-${i + 1}`}>
                    <span className="font-display text-sm italic text-gold">0{i + 1}</span>
                    <span className="text-sm font-light text-navy/85 md:text-base">{f}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.4}>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); scrollToSection("#contact"); }}
                data-testid="accommodation-enquire-button"
                className="group mt-10 inline-flex items-center gap-3 bg-navy px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-all duration-500 hover:bg-gold hover:text-navy"
              >
                Enquire Now
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
              </a>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {accommodation.strip.map((img, i) => (
            <Reveal key={img.src} delay={i * 0.08}>
              <div className="group aspect-[4/5] overflow-hidden" data-testid={`accommodation-strip-image-${i + 1}`}>
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            className="mt-20 flex flex-col items-center gap-10 border border-navy/10 bg-white p-8 md:mt-24 md:flex-row md:gap-14 md:p-12"
            data-testid="lodge-video-band"
          >
            <video
              src="/videos/lodge-tour.mp4"
              poster="/images/real/lodge-tour-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="aspect-[9/16] w-full max-w-[280px] shrink-0 object-cover"
              data-testid="lodge-tour-video"
            />
            <div>
              <Eyebrow>Take The Tour</Eyebrow>
              <h3 className="mt-5 font-display text-3xl font-medium text-navy md:text-5xl" data-testid="lodge-video-heading">
                Step inside the lodge
              </h3>
              <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-navy/75 md:text-base" data-testid="lodge-video-copy">
                A nine-second wander through the communal heart of the lodge —
                sofas by the stove, the long dining table and the sun pouring in.
                It plays on repeat; picture your group here.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
